import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import { IProduct } from 'src/app/models/products/product';
import { ProductsService } from 'src/app/services/products.service';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRIdGroups } from 'src/app/constants/signalr.constant';
import {
  FileExtension,
  FilenameIdentifier,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import { ProductDataPreviewService } from 'src/app/services/product-data-preview/product-data-preview.service';
import { PreviewProductDataResult } from 'src/app/models/product-data-preview/preview-product-data-result';
import { PreviewedProduct } from 'src/app/models/product-data-preview/previewed-product';
import { FileExtension as FileExtensionEnum } from 'src/app/models/product-data-filename-conventions/file-extension';
import { ProductUpdateComponent } from 'src/app/components/home/products/product-update/product-update.component';
import { ProductResult } from 'src/app/models/products/product-result';

interface ProductLabelCache {
  isLoading: boolean;
  labelData: Record<string, unknown> | null;
  errorMessage: string | null;
}

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

export type PreviewState = PreviewedProduct | 'loading' | 'error';

@Component({
  selector: 'app-product-label-data-list',
  templateUrl: './product-label-data-list.component.html',
  styleUrls: ['./product-label-data-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductLabelDataListComponent implements OnChanges {
  @Input() products: IProduct[] = [];
  @Input() batchId: string | null = null;
  @Input() filenameIdentifier: FilenameIdentifier | null = null;
  @Input() availableFormats: readonly FileExtension[] = [];
  @Input() blockOnMissingPdfc = false;

  expandedProducts: Set<string> = new Set();
  labelCache: Map<string, ProductLabelCache> = new Map();
  previewByProductId: Record<string, PreviewState> = {};

  private readonly destroyRef = inject(DestroyRef);
  private signalrJoined = false;

  constructor(
    private productsService: ProductsService,
    private signalrService: SignalrService,
    private previewService: ProductDataPreviewService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.maybeInvalidatePreviewCache(changes);
    this.preloadLabelData();
    this.joinSignalrGroup();
  }

  private maybeInvalidatePreviewCache(changes: SimpleChanges): void {
    const change: SimpleChanges[string] | undefined = changes['availableFormats'];
    if (change == null || change.firstChange) {
      return;
    }
    const before: readonly FileExtension[] | undefined = change.previousValue;
    const after: readonly FileExtension[] = change.currentValue ?? [];
    if (this.sameFormatSet(before, after)) {
      return;
    }
    this.previewByProductId = {};
  }

  private sameFormatSet(
    a: readonly FileExtension[] | undefined,
    b: readonly FileExtension[],
  ): boolean {
    if (a == null) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    const setA: Set<FileExtension> = new Set(a);
    return b.every((format: FileExtension) => setA.has(format));
  }

  get productsWithLabelData(): number {
    return this.products.filter((product) => product.productLabelDataGenerated).length;
  }

  get allHaveLabelData(): boolean {
    return this.products.every((product) => product.productLabelDataGenerated);
  }

  get anyExpanded(): boolean {
    return this.expandedProducts.size > 0;
  }

  toggleProduct(product: IProduct): void {
    const productId: string = product.id!;
    if (this.expandedProducts.has(productId)) {
      this.expandedProducts.delete(productId);
      return;
    }
    this.expandedProducts.add(productId);
    this.onRowExpand(product);
  }

  isProductExpanded(productId: string): boolean {
    return this.expandedProducts.has(productId);
  }

  expandAll(): void {
    this.products
      .filter((product) => product.id && product.productLabelDataGenerated)
      .forEach((product) => {
        this.expandedProducts.add(product.id!);
        this.onRowExpand(product);
      });
  }

  collapseAll(): void {
    this.expandedProducts = new Set();
  }

  getCache(productId: string): ProductLabelCache {
    return this.labelCache.get(productId) ?? { isLoading: false, labelData: null, errorMessage: null };
  }

  resolvedFilenameBase(product: IProduct): string | null {
    const raw: string | null = this.rawIdentifierValue(product);
    return raw != null && raw.trim().length > 0 ? raw : null;
  }

  isBlocked(product: IProduct): boolean {
    return this.blockOnMissingPdfc
      && this.filenameIdentifier != null
      && this.resolvedFilenameBase(product) == null;
  }

  blockedIdentifierLabel(): string {
    return this.filenameIdentifier ?? '';
  }

  openEditProduct(product: IProduct): void {
    this.dialog
      .open(ProductUpdateComponent, { data: product })
      .afterClosed()
      .subscribe(() => this.refreshProduct(product));
  }

  private refreshProduct(product: IProduct): void {
    if (product.id == null) {
      return;
    }
    this.productsService.detailsProduct(product.id).subscribe({
      next: (result: ProductResult) => {
        const refreshed: IProduct | undefined = result?.product;
        if (refreshed == null) {
          return;
        }
        this.products = this.products.map((existing: IProduct) =>
          existing.id === refreshed.id ? refreshed : existing,
        );
      },
    });
  }

  onRowExpand(product: IProduct): void {
    const productId: string = product.id!;
    if (this.availableFormats.length === 0) {
      return;
    }
    if (this.previewByProductId[productId] !== undefined) {
      return;
    }
    this.previewByProductId = { ...this.previewByProductId, [productId]: 'loading' };
    const formats: FileExtensionEnum[] = this.availableFormats.map(
      (format: FileExtension) => format as unknown as FileExtensionEnum,
    );
    this.previewService
      .preview([productId], formats)
      .subscribe({
        next: (result: PreviewProductDataResult) => {
          const previewed: PreviewedProduct | undefined = result.products.find(
            (candidate) => candidate.productId === productId,
          );
          this.previewByProductId = {
            ...this.previewByProductId,
            [productId]: previewed ?? 'error',
          };
        },
        error: () => {
          this.previewByProductId = { ...this.previewByProductId, [productId]: 'error' };
        },
      });
  }

  retryPreview(product: IProduct): void {
    const productId: string = product.id!;
    const { [productId]: _dropped, ...rest } = this.previewByProductId;
    this.previewByProductId = rest;
    this.onRowExpand(product);
  }

  isPreviewLoading(productId: string): boolean {
    return this.previewByProductId[productId] === 'loading';
  }

  isPreviewError(productId: string): boolean {
    return this.previewByProductId[productId] === 'error';
  }

  resolvedPreview(productId: string): PreviewedProduct | null {
    const state: PreviewState | undefined = this.previewByProductId[productId];
    if (state === undefined || state === 'loading' || state === 'error') {
      return null;
    }
    return state;
  }

  parseCsvContent(csv: string): ParsedCsv {
    const lines: string[] = csv.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
    if (lines.length === 0) {
      return { headers: [], rows: [] };
    }
    return {
      headers: this.parseCsvLine(lines[0]),
      rows: lines.slice(1).map((line: string) => this.parseCsvLine(line)),
    };
  }

  private parseCsvLine(line: string): string[] {
    const cells: string[] = [];
    const state: { current: string; inQuotes: boolean; skipNext: boolean } = {
      current: '',
      inQuotes: false,
      skipNext: false,
    };
    Array.from(line).forEach((char: string, index: number) => {
      if (state.skipNext) {
        state.skipNext = false;
        return;
      }
      if (char === '"' && state.inQuotes && line[index + 1] === '"') {
        state.current += '"';
        state.skipNext = true;
        return;
      }
      if (char === '"') {
        state.inQuotes = !state.inQuotes;
        return;
      }
      if (char === ',' && !state.inQuotes) {
        cells.push(state.current);
        state.current = '';
        return;
      }
      state.current += char;
    });
    cells.push(state.current);
    return cells;
  }

  private rawIdentifierValue(product: IProduct): string | null {
    if (this.filenameIdentifier === 'Reference') {
      return product.reference;
    }
    if (this.filenameIdentifier === 'Description') {
      return product.description;
    }
    if (this.filenameIdentifier === 'Barcode') {
      return product.barcode;
    }
    return null;
  }

  // --- SignalR ---

  private joinSignalrGroup(): void {
    if (this.signalrJoined || !this.batchId) {
      return;
    }
    this.signalrJoined = true;

    SignalrGroup.joinWithId({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRIdGroups.ProductsList,
      id: this.batchId,
      data$: this.signalrService.productsData,
      onData: (data: IProduct) => this.onProductUpdate(data),
    });
  }

  private onProductUpdate(updated: IProduct): void {
    if (!updated.id) {
      return;
    }

    const productIds = this.products.map((product) => product.id);
    if (!productIds.includes(updated.id)) {
      return;
    }

    const existing = this.products.find((product) => product.id === updated.id);
    const newlyGenerated = updated.productLabelDataGenerated && !existing?.productLabelDataGenerated;

    this.products = this.products.map((product) =>
      product.id === updated.id ? updated : product,
    );

    if (newlyGenerated && !this.labelCache.has(updated.id)) {
      this.loadProductLabelData(updated.id);
    }
  }

  // --- Label data loading ---

  private preloadLabelData(): void {
    this.products
      .filter((product) => product.id && product.productLabelDataGenerated && !this.labelCache.has(product.id))
      .forEach((product) => this.loadProductLabelData(product.id!));
  }

  private loadProductLabelData(productId: string): void {
    this.labelCache.set(productId, { isLoading: true, labelData: null, errorMessage: null });

    this.productsService.downloadProductLabelData(productId).pipe(
      switchMap((blob) => from(blob.text())),
    ).subscribe({
      next: (text) => {
        this.labelCache.set(productId, {
          isLoading: false,
          labelData: JSON.parse(text),
          errorMessage: null,
        });
      },
      error: () => {
        this.labelCache.set(productId, {
          isLoading: false,
          labelData: null,
          errorMessage: 'Failed to load product label data',
        });
      },
    });
  }
}
